import { useEffect, useMemo, useState } from 'react'
import { PlayCircle } from 'lucide-react'
import { VideoEmbed } from '@/components/video-embed'
import { cn } from '@/lib/utils'
import { getProductGalleryMedia } from '@/lib/product-media'
import type { Product, ProductVariant } from '@/types/database'
import type { ProductMediaItem } from '@/types/product-ui'

export interface MediaInfo {
  hasReviewVideo: boolean
  jumpToReviewVideo: () => void
}

interface ProductMediaGalleryProps {
  product: Product
  selectedVariant?: ProductVariant | null
  className?: string
  variant?: 'hero' | 'detail'
  onMediaInfo?: (info: MediaInfo) => void
  excludeReviewVideo?: boolean
}

interface MediaThumbProps {
  item: ProductMediaItem
  isActive: boolean
  onSelect: () => void
  title: string
  galleryVariant: 'hero' | 'detail'
}

function MediaThumb({ item, isActive, onSelect, title, galleryVariant }: MediaThumbProps) {
  const isHero = galleryVariant === 'hero'
  return (
    <button
      type="button"
      onClick={onSelect}
      aria-label={item.kind === 'video' ? 'View review video' : `View ${item.label || 'image'}`}
      className={cn(
        'relative flex-shrink-0 overflow-hidden border-2 transition-all focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-red-400',
        isHero ? 'rounded-xl' : 'rounded-lg',
        isActive
          ? 'border-red-500 shadow-md'
          : isHero
            ? 'border-zinc-200 hover:border-zinc-300'
            : 'border-zinc-200 opacity-60 hover:opacity-100'
      )}
    >
      {item.thumbnailUrl || item.kind === 'image' ? (
        <img
          src={item.thumbnailUrl || item.url}
          alt={item.kind === 'video' ? `${title} review video thumbnail` : `${title} product image`}
          loading="lazy"
          decoding="async"
          className={isHero ? 'h-16 w-16 object-cover md:h-20 md:w-20' : 'size-20 object-cover'}
        />
      ) : (
        <div className={cn(
          'flex items-center justify-center bg-zinc-100 text-xs font-medium text-zinc-500',
          isHero ? 'h-16 w-16 md:h-20 md:w-20' : 'size-20'
        )}>
          Media
        </div>
      )}
      {item.kind === 'video' && (
        <span className="absolute inset-0 flex items-center justify-center bg-black/35">
          <PlayCircle className="size-6 text-white" />
        </span>
      )}
    </button>
  )
}

export function ProductMediaGallery({
  product,
  selectedVariant,
  className,
  variant = 'detail',
  onMediaInfo,
  excludeReviewVideo = false,
}: ProductMediaGalleryProps) {
  const [selectedMediaIndex, setSelectedMediaIndex] = useState(0)
  const isHero = variant === 'hero'

  const allMediaItems = useMemo(() => getProductGalleryMedia(product), [product])

  const baseMediaItems = useMemo(() => {
    if (!excludeReviewVideo) return allMediaItems
    // Filter out the review video (marked isReview, or first video as fallback)
    const reviewIdx = allMediaItems.findIndex(item => item.kind === 'video' && item.isReview)
    if (reviewIdx >= 0) return allMediaItems.filter((_, i) => i !== reviewIdx)
    const firstVideoIdx = allMediaItems.findIndex(item => item.kind === 'video')
    if (firstVideoIdx >= 0) return allMediaItems.filter((_, i) => i !== firstVideoIdx)
    return allMediaItems
  }, [allMediaItems, excludeReviewVideo])

  // If selected variant has an image, prepend it (deduplicated)
  const mediaItems = useMemo(() => {
    if (!selectedVariant?.image_url) return baseMediaItems
    const variantUrl = selectedVariant.image_url.trim()
    const filtered = baseMediaItems.filter(
      item => !(item.kind === 'image' && item.url.trim() === variantUrl)
    )
    const variantItem: ProductMediaItem = {
      kind: 'image',
      url: variantUrl,
      label: 'Variant',
    }
    return [variantItem, ...filtered]
  }, [baseMediaItems, selectedVariant])

  const selectedMedia = mediaItems[selectedMediaIndex] ?? mediaItems[0]

  // Find review video index
  const reviewVideoIndex = useMemo(() => {
    const reviewIdx = mediaItems.findIndex(item => item.kind === 'video' && item.isReview)
    if (reviewIdx >= 0) return reviewIdx
    return mediaItems.findIndex(item => item.kind === 'video')
  }, [mediaItems])

  const hasReviewVideo = reviewVideoIndex >= 0
  const canShowVideoShortcut = selectedMedia?.kind === 'image' && hasReviewVideo

  // Reset index on product or variant change
  useEffect(() => {
    setSelectedMediaIndex(0)
  }, [product.id, selectedVariant?.id])

  // Expose media info to parent
  useEffect(() => {
    onMediaInfo?.({
      hasReviewVideo,
      jumpToReviewVideo: () => {
        if (hasReviewVideo) setSelectedMediaIndex(reviewVideoIndex)
      },
    })
  }, [hasReviewVideo, reviewVideoIndex, onMediaInfo])

  return (
    <div className={cn('space-y-4', className)}>
      <div className={cn(
        'relative overflow-hidden',
        isHero
          ? 'rounded-2xl border border-zinc-200 bg-white shadow-xl'
          : 'rounded-xl shadow-xl overflow-hidden'
      )}>
        {selectedMedia ? (
          selectedMedia.kind === 'video' ? (
            <VideoEmbed
              url={selectedMedia.url}
              thumbnailUrl={selectedMedia.thumbnailUrl}
              title={product.title}
              className="rounded-none"
              aspectRatio="16/9"
            />
          ) : (
            <div className="relative aspect-[16/10]">
              <img
                src={selectedMedia.url}
                alt={`${product.title} ${isHero ? 'featured' : 'product'} image`}
                loading="eager"
                decoding="async"
                className="h-full w-full object-cover"
              />
              {canShowVideoShortcut && (
                <button
                  type="button"
                  onClick={() => setSelectedMediaIndex(reviewVideoIndex)}
                  className="absolute bottom-4 right-4 inline-flex items-center gap-2 rounded-full border border-white/30 bg-black/70 px-3 py-1.5 text-sm font-medium text-white transition-colors hover:bg-black/80 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-red-400"
                >
                  <PlayCircle className="size-4" />
                  Watch review
                </button>
              )}
            </div>
          )
        ) : (
          <div className="flex aspect-[16/10] items-center justify-center bg-zinc-100">
            <span className="text-sm text-zinc-500">Media coming soon</span>
          </div>
        )}
      </div>

      {mediaItems.length > 1 && (
        <div className="flex gap-2 overflow-x-auto pb-1">
          {mediaItems.map((item, index) => (
            <MediaThumb
              key={`${item.kind}-${item.url}-${index}`}
              item={item}
              isActive={index === selectedMediaIndex}
              onSelect={() => setSelectedMediaIndex(index)}
              title={product.title}
              galleryVariant={variant}
            />
          ))}
        </div>
      )}
    </div>
  )
}
